import React, { useState } from 'react';
import { MachineData, LogEntry } from '../types/types';
import { formatNumber } from '../utils/formatters';
import { Calendar, Filter, Download, Search } from 'lucide-react';
interface HistoryViewProps {
  machine: MachineData;
}
export const HistoryView: React.FC<HistoryViewProps> = ({
  machine
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });
  const filteredLogs = machine.logs.filter(log => {
    const matchesSearch = log.message.toLowerCase().includes(searchTerm.toLowerCase()) || log.component.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || log.type === filterStatus;
    return matchesSearch && matchesStatus;
  });
  return <div className="space-y-6" data-id="element-199">
      <div className="flex items-center justify-between" data-id="element-200">
        <div data-id="element-201">
          <h2 className="text-2xl font-bold text-white" data-id="element-202">
            {machine.name} - Historische Daten
          </h2>
          <p className="text-gray-400" data-id="element-203">
            Protokolle und Analysen vergangener Aktivitäten
          </p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors" data-id="element-204">
          <Download size={16} data-id="element-205" />
          <span data-id="element-206">Exportieren</span>
        </button>
      </div>
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60" data-id="element-207">
        <div className="flex flex-col md:flex-row gap-4 mb-6" data-id="element-208">
          <div className="flex-1" data-id="element-209">
            <div className="relative" data-id="element-210">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-211" />
              <input type="text" placeholder="Suche nach Ereignissen oder Komponenten..." className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-212" />
            </div>
          </div>
          <div className="flex gap-4" data-id="element-213">
            <div className="relative" data-id="element-214">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-215" />
              <select className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-id="element-216">
                <option value="all" data-id="element-217">Alle Status</option>
                <option value="info" data-id="element-218">Info</option>
                <option value="warning" data-id="element-219">Warnung</option>
                <option value="error" data-id="element-220">Fehler</option>
              </select>
            </div>
            <div className="relative" data-id="element-221">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-222" />
              <input type="date" className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" value={dateRange.from} onChange={e => setDateRange({
              ...dateRange,
              from: e.target.value
            })} data-id="element-223" />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-224">
          <table className="w-full" data-id="element-225">
            <thead data-id="element-226">
              <tr className="text-left border-b border-gray-700" data-id="element-227">
                <th className="pb-3 pr-6 text-gray-400" data-id="element-228">Zeitstempel</th>
                <th className="pb-3 pr-6 text-gray-400" data-id="element-229">Typ</th>
                <th className="pb-3 pr-6 text-gray-400" data-id="element-230">Komponente</th>
                <th className="pb-3 text-gray-400" data-id="element-231">Nachricht</th>
              </tr>
            </thead>
            <tbody data-id="element-232">
              {filteredLogs.map((log: LogEntry, index) => <tr key={index} className="border-b border-gray-700/50" data-id="element-233">
                  <td className="py-3 pr-6 text-sm text-gray-300" data-id="element-234">
                    {log.timestamp}
                  </td>
                  <td className="py-3 pr-6" data-id="element-235">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${log.type === 'info' ? 'bg-blue-500/20 text-blue-400' : log.type === 'warning' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`} data-id="element-236">
                      {log.type === 'info' ? 'Info' : log.type === 'warning' ? 'Warnung' : 'Fehler'}
                    </span>
                  </td>
                  <td className="py-3 pr-6 font-medium" data-id="element-237">{log.component}</td>
                  <td className="py-3 text-gray-300" data-id="element-238">{log.message}</td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60" data-id="element-239">
        <h3 className="text-xl font-medium mb-4 text-gray-200" data-id="element-240">
          Leistungsanalyse
        </h3>
        <div className="space-y-4" data-id="element-241">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-242">
            <div className="bg-gray-700/50 p-4 rounded-lg" data-id="element-243">
              <h4 className="text-sm text-gray-400 mb-1" data-id="element-244">
                Durchschnittliche Effizienz
              </h4>
              <p className="text-2xl font-bold" data-id="element-245">
                {machine.analytics.averageEfficiency}%
              </p>
            </div>
            <div className="bg-gray-700/50 p-4 rounded-lg" data-id="element-246">
              <h4 className="text-sm text-gray-400 mb-1" data-id="element-247">
                Betriebszeit (Gesamt)
              </h4>
              <p className="text-2xl font-bold" data-id="element-248">
                {formatNumber(machine.analytics.totalOperatingHours)}h
              </p>
            </div>
            <div className="bg-gray-700/50 p-4 rounded-lg" data-id="element-249">
              <h4 className="text-sm text-gray-400 mb-1" data-id="element-250">Fehlerrate</h4>
              <p className="text-2xl font-bold" data-id="element-251">
                {machine.analytics.errorRate}%
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
};